import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useForm } from "react-hook-form";
import { promptTemplateApi } from "@/apis/promptTemplate";
import Router from "@/constants/router";
import { PromptTemplateDto, UpdatePromptTemplateDto } from "@/dto/PromptTemplateDto";

interface FormData extends UpdatePromptTemplateDto {}

export default function usePromptTemplateDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<PromptTemplateDto | null>(null);

  const form = useForm<FormData>({
    defaultValues: {
      templateKey: "",
      templateName: "",
      templateDescription: "",
      templateCategory: "",
      systemPrompt: "",
      userPromptTemplate: "",
      assistantPrompt: "",
      promptVariables: "",
      aiModelProvider: "OPENAI",
      aiModelName: "gpt-4o",
      aiModelTemperature: 0.7,
      aiMaxTokens: 4000,
      aiModelConfig: "",
      useCondition: "",
      priorityOrder: 1,
      isActive: true,
    },
  });

  const fetchData = useCallback(async () => {
    if (!id) return;
    
    setLoading(true);
    try {
      const result = await promptTemplateApi.getDetail(Number(id));
      setData(result);
      
      // Handle aiModelTemperature: convert string to number if needed
      const temperature = typeof result.aiModelTemperature === 'string' 
        ? parseFloat(result.aiModelTemperature) 
        : result.aiModelTemperature;

      // Handle promptVariables: convert object to JSON string, handle empty object
      const promptVariablesStr = result.promptVariables 
        ? (Object.keys(result.promptVariables).length > 0 
            ? JSON.stringify(result.promptVariables, null, 2) 
            : "")
        : "";

      // Handle aiModelConfig: convert object to JSON string, handle null
      const aiModelConfigStr = result.aiModelConfig 
        ? JSON.stringify(result.aiModelConfig, null, 2) 
        : "";

      form.reset({
        templateKey: result.templateKey,
        templateName: result.templateName,
        templateDescription: result.templateDescription || "",
        templateCategory: result.templateCategory,
        systemPrompt: result.systemPrompt || "",
        userPromptTemplate: result.userPromptTemplate || "",
        assistantPrompt: result.assistantPrompt || "",
        promptVariables: promptVariablesStr,
        aiModelProvider: result.aiModelProvider || "OPENAI",
        aiModelName: result.aiModelName || "",
        aiModelTemperature: temperature || 0.7,
        aiMaxTokens: result.aiMaxTokens || 4000,
        aiModelConfig: aiModelConfigStr,
        useCondition: result.useCondition || "",
        priorityOrder: result.priorityOrder || 1,
        isActive: result.isActive ?? true,
      });
    } catch (error: any) {
      enqueueSnackbar(error.message || "Failed to fetch prompt template", { variant: "error" });
      navigate(Router.PromptTemplate);
    } finally {
      setLoading(false);
    }
  }, [id, form, navigate, enqueueSnackbar]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = useCallback(async (formData: FormData) => {
    if (!id) return;
    
    setLoading(true);
    try {
      const updateData: UpdatePromptTemplateDto = {
        ...formData,
        promptVariables: formData.promptVariables ? JSON.parse(formData.promptVariables as string) : null,
        aiModelConfig: formData.aiModelConfig ? JSON.parse(formData.aiModelConfig as string) : null,
      };

      const result = await promptTemplateApi.update(Number(id), updateData);
      setData(result);
      enqueueSnackbar("Prompt template updated successfully.", { variant: "success" });
    } catch (error: any) {
      enqueueSnackbar(error.message || "Failed to update prompt template", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [id, enqueueSnackbar]);

  const handleDelete = useCallback(async () => {
    if (!id || !data) return;
    
    if (!confirm("Are you sure you want to delete this prompt template?")) return;
    
    try {
      await promptTemplateApi.delete(Number(id));
      enqueueSnackbar("Prompt template deleted successfully.", { variant: "success" });
      navigate(Router.PromptTemplate);
    } catch (error: any) {
      enqueueSnackbar(error.message || "Failed to delete prompt template", { variant: "error" });
    }
  }, [id, data, navigate, enqueueSnackbar]);

  const handleCancel = useCallback(() => {
    navigate(Router.PromptTemplate);
  }, [navigate]);

  return {
    data,
    form,
    loading,
    handleSubmit,
    handleDelete,
    handleCancel,
  };
}